import { Dayjs } from 'dayjs';
import { IDateRangePickerDate } from '../../shared/components/date-range-picker/date-range-picker.interface';
import { EItemPriority } from '../../shared/components/item-priority/item-priority.interface';
import { IDropdownOption } from '../../shared/components/mat-dropdown/mat-dropdown.component';
import { IUser } from '../../state/user/user.interface';
import { IBoard } from '../settings/boards/boards.interface';
import {
  IBoardItemConfigurationBase,
  IBoardItemConfigurationJoins,
  IFieldSetBase,
  IItemWorkInstructionAssignment,
} from '../settings/fields-field-sets/field-sets/field-sets.interface';
import { EFieldType } from '../settings/fields-field-sets/fields/fields.interface';
import { IItemComment } from './items-modal/item-comment/item-comment.interface';
import { ERecursionLimitType } from './items-modal/recurring-item-configuration/recurring-item-configuration.interface';

export interface IItemBase<DateType extends Dayjs | string = Dayjs> {
  assigneeId: number | null;
  reporterId: number | null;
  boardId: number;
  boardItemConfigurationId: number;
  createdAt: string;
  createdBy: number;
  currentWorkflowStepId: number;
  description: string | null;
  dueDate: string | null;
  fieldSetId: number;
  id: number;
  isQuick: boolean;
  key: string;
  name: string;
  changedAt: string;
  changedBy: number;
  eventDate: string | null;
  shiftId: number | null;
  shift: IDropdownOption | null;
  escalatedBoardId: number;
  values: TItemCustomFieldValue<DateType>[];
  itemRecurrenceId: number | null;
  itemComments?: IItemComment[] | null;
  priority: EItemPriority | null;
  strikeCount: number;
}

export interface IItemRecurrence {
  baseWorkflowStepId: number;
  id: number;
  cron: string;
  endDateLimit?: string;
  numberLimit?: number;
  selectedLimitType: ERecursionLimitType;
}

export type TItemCustomFieldValue<DateType extends Dayjs | string = Dayjs> =
  | ICustomOptionFieldValue
  | ICustomDateRangeFieldValue<DateType>
  | ICustomTextFieldValue;

export type TBoardItemConfiguration = IBoardItemConfigurationBase &
  IBoardItemConfigurationJoins & {
    workflow: TWorkflow;
  };

export interface IItemJoins<DateType extends Dayjs | string = Dayjs> extends IItemBase<DateType> {
  assignee: IUser | null;
  board: IBoard;
  boardItemConfiguration: TBoardItemConfiguration;
  itemGuideAssignments: IItemWorkInstructionAssignment[] | null;
  currentWorkflowStep: IWorkflowStepBase;
  fieldSet: IFieldSetBase;
  reporter: IUser | null;
  sourceItem: IItemJoins<DateType>;
  sourceItems: IItemJoins<DateType>[];
  targetItem: IItemJoins<DateType>;
  targetItems: IItemJoins<DateType>[];
  itemRecurrence: IItemRecurrence;
  escalatedBoard: IBoard | null;
}

export interface IItemCustomFieldBase {
  fieldId: number;
  fieldName: string;
  type: EFieldType;
}

export interface ICustomOptionFieldValue extends IItemCustomFieldBase {
  selectedValue: IDropdownOption[] | null;
  type:
    | EFieldType.singleSelectDropdown
    | EFieldType.multiSelectDropdown
    | EFieldType.singleSelectCheckbox
    | EFieldType.multiSelectCheckbox;
}

export interface ICustomDateRangeFieldValue<DateType extends Dayjs | string = Dayjs> extends IItemCustomFieldBase {
  selectedValue: IDateRangePickerDate<DateType>;
  type: EFieldType.date;
}

export interface ICustomTextFieldValue extends IItemCustomFieldBase {
  selectedValue: string;
  type: EFieldType.text | EFieldType.textArea | EFieldType.wiki;
}

export interface IWorkflowStepBase {
  id: number;
  isDefault: boolean;
  name: string;
  statusCategory: EWorkflow;
}

export type TStatusCategory = 'done' | 'inProgress' | 'todo';

export type TUrlItemScope = 'all' | 'self' | 'escalated' | null;

export interface IBoardItemsInitialData {
  boardTreeStructure: IBoardTreeStructure[];
  initialBoard?: IDropdownOption & { key: string };
  item?: IItemBase<string>;
}

export interface IBoardTreeStructure {
  children: IBoardTreeStructure[];
  id: number;
}

export interface IWorkflowBase {
  id: number;
  name: string;
  startWorkflowStepId?: number | null;
}

export interface IWorkflowJoins {
  workflowStepAssignments: TWorkflowStepAssignment[];
}

export interface IWorkflowStepAssignmentBase {
  id: number;
  nextWorkflowStepId: number;
  workflowStepId: number;
}

export interface IWorkflowStepAssignmentJoins {
  nextWorkflowStep?: IWorkflowStepBase | null;
  workflowStep: IWorkflowStepBase;
}

export type TWorkflow = IWorkflowBase & IWorkflowJoins;
export type TWorkflowStepAssignment = IWorkflowStepAssignmentBase & IWorkflowStepAssignmentJoins;

export type TUrlResetType = 'itemKey' | 'full';

export enum EWorkflow {
  done = 'done',
  inProgress = 'inProgress',
  todo = 'todo',
}

/**
 * @deprecated **This is retained strictly for legacy internal ID mapping requirements.**
 * For new implementations, please use `EWorkflowStatus` (or the appropriate alternative).
 */
export enum EWorkflowIds {
  todo = 1,
  inProgress = 2,
  done = 3,
}

export enum EWorkflowIcon {
  adjust = 'adjust',
  schedule = 'schedule',
  task_alt = 'task_alt',
}

export enum EStatusBadgeExtraCategory {
  late = 'late',
  paused = 'paused',
}

export type TStatusBadgeCategory = EWorkflow | EStatusBadgeExtraCategory;

export enum EChipColor {
  blue = 'blue',
  gray = 'gray',
  green = 'green',
}

export enum EUserAssignee {
  unassignedUser = -1,
}
